import React, { useEffect, useState, useCallback } from 'react';
import { getAnalyticsApiBaseUrl } from '../../../config/api';
import { fetchWithAuth } from '../../../utils/auth';
import LoadingSpinner from '../components/LoadingSpinner';
import DatabaseHistoryCharts from './DatabaseHistoryCharts';

interface MemberHealth {
  name: string;
  host: string;
  state: string | null;
  health: 0 | 1;
  uptime: number;
  version: string | null;
  isArbiter: boolean;
  reachable: boolean;
  error?: string;
  connections: { current: number; active: number; available: number; totalCreated: number } | null;
  memory: {
    rssMB: number; virtualMB: number;
    cacheUsedGB: number; cacheMaxGB: number; cacheUsedPct: number;
    cacheDirtyMB: number; cacheHitRatePct: number;
  } | null;
  storage: { dataGB: number; storageGB: number; indexGB: number; totalGB: number; collections: number; indexes: number } | null;
  opsAvg: { insertPerSec: number; queryPerSec: number; updatePerSec: number; deletePerSec: number; getmorePerSec: number } | null;
  replication: { optimeMs: number | null; lagSeconds: number | null } | null;
}

interface SlowOp {
  secs: number;
  ns: string;
  op: string;
  plan: string;
  docsExamined: number;
  keysExamined: number;
}

interface Alert {
  severity: 'red' | 'yellow';
  member: string;
  metric: string;
  value: string;
  message: string;
}

interface HealthResponse {
  set: string;
  generatedAt: string;
  members: MemberHealth[];
  slowOps: SlowOp[];
  alerts: Alert[];
}

interface RedisHealth {
  reachable: boolean;
  error?: string;
  version: string | null;
  uptimeSeconds: number;
  role: string;
  connectedSlaves: number;
  clients: { connected: number; max: number };
  memory: {
    usedBytes: number; usedHuman: string; rssBytes: number; peakBytes: number; maxBytes: number;
    usedPct: number; fragmentationRatio: number; policy: string;
  };
  stats: {
    totalConnectionsReceived: number; rejectedConnections: number; totalCommandsProcessed: number;
    keyspaceHits: number; keyspaceMisses: number; hitRatePct: number; evictedKeys: number; expiredKeys: number;
  };
  keyspace: Array<{ db: string; keys: number; expires: number }>;
  alerts: Array<{ severity: 'red' | 'yellow'; metric: string; message: string; value: string }>;
}

// Color mapping for member state
const stateColor = (state: string | null, reachable: boolean): { bg: string; text: string; ring: string } => {
  if (!reachable) return { bg: 'bg-red-500/10', text: 'text-red-400', ring: 'ring-red-500/40' };
  switch (state) {
    case 'PRIMARY':   return { bg: 'bg-emerald-500/10', text: 'text-emerald-400', ring: 'ring-emerald-500/40' };
    case 'SECONDARY': return { bg: 'bg-sky-500/10',     text: 'text-sky-400',     ring: 'ring-sky-500/40' };
    case 'ARBITER':   return { bg: 'bg-violet-500/10',  text: 'text-violet-400',  ring: 'ring-violet-500/40' };
    default:          return { bg: 'bg-amber-500/10',   text: 'text-amber-400',   ring: 'ring-amber-500/40' };
  }
};

// Color mapping for percentage metrics (lower = better)
const pctColor = (pct: number, yellow = 60, red = 80): string => {
  if (pct >= red) return 'text-red-400';
  if (pct >= yellow) return 'text-amber-400';
  return 'text-emerald-400';
};

const formatUptime = (s: number): string => {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
};

const Metric: React.FC<{ label: string; value: string | number; color?: string; sub?: string }> = ({ label, value, color, sub }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    <p className={`text-lg font-bold mt-0.5 ${color || 'text-white'}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const MemberCard: React.FC<{ m: MemberHealth }> = ({ m }) => {
  const sc = stateColor(m.state, m.reachable);
  const connTotal = m.connections ? m.connections.current + m.connections.available : 0;
  const connPct = connTotal > 0 ? Math.round((m.connections!.current / connTotal) * 100) : 0;

  return (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 ring-2 ${sc.ring}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-white capitalize">{m.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${sc.bg} ${sc.text}`}>
              {m.reachable ? (m.state || 'UNKNOWN') : '🚨 DOWN'}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{m.host}</p>
        </div>
        {m.reachable && (
          <div className="text-right">
            <p className="text-xs text-gray-500">v{m.version}</p>
            <p className="text-xs text-gray-400">up {formatUptime(m.uptime)}</p>
          </div>
        )}
      </div>

      {!m.reachable && (
        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          {m.error || 'Unreachable'}
        </div>
      )}

      {m.reachable && m.connections && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Metric
            label="Connections"
            value={formatNumber(m.connections.current)}
            sub={`${connPct}% of pool · active ${m.connections.active}`}
            color={pctColor(connPct, 60, 80)}
          />
          <Metric
            label="Memory (RSS)"
            value={`${(m.memory!.rssMB / 1024).toFixed(1)} GB`}
            sub={`virt ${(m.memory!.virtualMB / 1024).toFixed(1)} GB`}
          />
          {!m.isArbiter && (
            <Metric
              label="WT Cache"
              value={`${m.memory!.cacheUsedGB} / ${m.memory!.cacheMaxGB} GB`}
              sub={`${m.memory!.cacheUsedPct}% used · ${m.memory!.cacheHitRatePct}% hit rate`}
              color={pctColor(m.memory!.cacheUsedPct, 75, 90)}
            />
          )}
          {m.isArbiter && (
            <Metric label="Role" value="Arbiter" sub="vote-only, no data" />
          )}
        </div>
      )}

      {m.reachable && m.opsAvg && !m.isArbiter && (
        <div className="border-t border-white/5 pt-3 mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Op rates (avg/sec since uptime start)</p>
          <div className="grid grid-cols-5 gap-2 text-center">
            <div><p className="text-xs text-gray-400">QUERY</p><p className="text-sm text-white font-semibold">{Math.round(m.opsAvg.queryPerSec)}</p></div>
            <div><p className="text-xs text-gray-400">INSERT</p><p className="text-sm text-white font-semibold">{Math.round(m.opsAvg.insertPerSec)}</p></div>
            <div><p className="text-xs text-gray-400">UPDATE</p><p className="text-sm text-white font-semibold">{Math.round(m.opsAvg.updatePerSec)}</p></div>
            <div><p className="text-xs text-gray-400">DELETE</p><p className="text-sm text-white font-semibold">{m.opsAvg.deletePerSec.toFixed(1)}</p></div>
            <div><p className="text-xs text-gray-400">GETMORE</p><p className="text-sm text-white font-semibold">{Math.round(m.opsAvg.getmorePerSec)}</p></div>
          </div>
        </div>
      )}

      {m.reachable && m.storage && !m.isArbiter && (
        <div className="border-t border-white/5 pt-3 mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
            Storage on disk
            <span className="text-gray-600 normal-case ml-2 font-normal">
              (data {m.storage.dataGB} GB → compressed {m.storage.storageGB} GB · {(m.storage.dataGB / Math.max(m.storage.storageGB, 0.01)).toFixed(1)}× ratio)
            </span>
          </p>
          <div className="grid grid-cols-4 gap-2">
            <Metric label="Data files" value={`${m.storage.storageGB} GB`} sub="compressed" />
            <Metric label="Indexes" value={`${m.storage.indexGB} GB`} sub={`${m.storage.indexes} idx`} />
            <Metric label="Mongo total" value={`${(m.storage.storageGB + m.storage.indexGB).toFixed(1)} GB`} sub="data + indexes" />
            <Metric label="Collections" value={m.storage.collections} />
          </div>
        </div>
      )}

      {m.reachable && m.replication?.lagSeconds !== null && m.replication?.lagSeconds !== undefined && (
        <div className="border-t border-white/5 pt-3">
          <Metric
            label="Replication lag (vs primary)"
            value={`${m.replication.lagSeconds}s`}
            color={m.replication.lagSeconds > 30 ? 'text-red-400' : m.replication.lagSeconds > 5 ? 'text-amber-400' : 'text-emerald-400'}
          />
        </div>
      )}
    </div>
  );
};

const formatBytes = (b: number): string => {
  if (b >= 1e9) return (b / 1e9).toFixed(2) + ' GB';
  if (b >= 1e6) return (b / 1e6).toFixed(1) + ' MB';
  if (b >= 1e3) return (b / 1e3).toFixed(1) + ' KB';
  return `${b} B`;
};

const RedisCard: React.FC<{ r: RedisHealth }> = ({ r }) => {
  const ring = !r.reachable ? 'ring-red-500/40' :
               r.memory.usedPct > 90 ? 'ring-red-500/40' :
               r.memory.usedPct > 75 ? 'ring-amber-500/40' :
               'ring-rose-500/40';
  const memColor = r.memory.usedPct > 90 ? 'text-red-400' :
                   r.memory.usedPct > 75 ? 'text-amber-400' : 'text-emerald-400';
  const clientPct = r.clients.max > 0 ? Math.round((r.clients.connected / r.clients.max) * 100) : 0;

  return (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 ring-2 ${ring}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-white">Redis (ElastiCache / Valkey)</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.reachable ? 'bg-rose-500/10 text-rose-400' : 'bg-red-500/10 text-red-400'}`}>
              {r.reachable ? r.role.toUpperCase() : '🚨 DOWN'}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">terraform-tezda-redis · single-node</p>
        </div>
        {r.reachable && (
          <div className="text-right">
            <p className="text-xs text-gray-500">v{r.version}</p>
            <p className="text-xs text-gray-400">up {formatUptime(r.uptimeSeconds)}</p>
          </div>
        )}
      </div>

      {!r.reachable && (
        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          {r.error || 'Unreachable'}
        </div>
      )}

      {r.reachable && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Metric label="Memory" value={`${formatBytes(r.memory.usedBytes)} / ${formatBytes(r.memory.maxBytes)}`} sub={`${r.memory.usedPct}% · peak ${formatBytes(r.memory.peakBytes)}`} color={memColor} />
            <Metric label="Clients" value={formatNumber(r.clients.connected)} sub={`${clientPct}% of ${formatNumber(r.clients.max)} max`} color={pctColor(clientPct, 60, 80)} />
            <Metric label="Hit Rate" value={`${r.stats.hitRatePct}%`} sub={`${formatNumber(r.stats.keyspaceHits)} hits`} color={r.stats.hitRatePct > 90 ? 'text-emerald-400' : r.stats.hitRatePct > 80 ? 'text-amber-400' : 'text-red-400'} />
          </div>

          <div className="border-t border-white/5 pt-3 mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Throughput &amp; Eviction</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div><p className="text-xs text-gray-400">CMDS PROCESSED</p><p className="text-sm text-white font-semibold">{formatNumber(r.stats.totalCommandsProcessed)}</p></div>
              <div><p className="text-xs text-gray-400">CONNS RECEIVED</p><p className="text-sm text-white font-semibold">{formatNumber(r.stats.totalConnectionsReceived)}</p></div>
              <div><p className="text-xs text-gray-400">EVICTED KEYS</p><p className={`text-sm font-semibold ${r.stats.evictedKeys > 1_000_000 ? 'text-amber-400' : 'text-white'}`}>{formatNumber(r.stats.evictedKeys)}</p></div>
              <div><p className="text-xs text-gray-400">REJECTED</p><p className={`text-sm font-semibold ${r.stats.rejectedConnections > 0 ? 'text-red-400' : 'text-white'}`}>{r.stats.rejectedConnections}</p></div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-3 mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Replication &amp; Health</p>
            <div className="grid grid-cols-3 gap-2">
              <Metric label="Replicas" value={r.connectedSlaves} sub={r.connectedSlaves === 0 ? '⚠ no failover' : 'OK'} color={r.connectedSlaves === 0 ? 'text-amber-400' : 'text-emerald-400'} />
              <Metric label="Eviction Policy" value={r.memory.policy} />
              <Metric label="Fragmentation" value={r.memory.fragmentationRatio.toFixed(2)} sub={r.memory.fragmentationRatio > 1.5 ? 'high' : 'normal'} color={r.memory.fragmentationRatio > 1.5 ? 'text-amber-400' : 'text-emerald-400'} />
            </div>
          </div>

          {r.keyspace.length > 0 && (
            <div className="border-t border-white/5 pt-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Keyspace ({r.keyspace.length} dbs)</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-400">
                    <th className="font-medium pr-4">DB</th><th className="font-medium pr-4 text-right">Keys</th><th className="font-medium text-right">With TTL</th>
                  </tr></thead>
                  <tbody>
                    {r.keyspace.slice(0, 6).map(d => (
                      <tr key={d.db} className="text-white/80">
                        <td className="pr-4 py-1">{d.db}</td>
                        <td className="pr-4 py-1 text-right">{formatNumber(d.keys)}</td>
                        <td className="py-1 text-right text-gray-400">{formatNumber(d.expires)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DatabaseTab: React.FC = () => {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [redis, setRedis] = useState<RedisHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      setError(null);
      const base = getAnalyticsApiBaseUrl();
      const [mongoRes, redisRes] = await Promise.all([
        fetchWithAuth(`${base}/mongo/health`, { cache: 'no-store' }),
        fetchWithAuth(`${base}/redis/health`, { cache: 'no-store' }),
      ]);
      if (!mongoRes.ok) throw new Error(`mongo HTTP ${mongoRes.status}`);
      const mongoJson: HealthResponse = await mongoRes.json();
      const redisJson: RedisHealth = redisRes.ok ? await redisRes.json() : { reachable: false, error: `HTTP ${redisRes.status}` } as RedisHealth;
      setData(mongoJson);
      setRedis(redisJson);
      setLastFetch(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 10_000);   // refresh every 10 s
    return () => clearInterval(id);
  }, [fetchHealth]);

  if (loading && !data) return <LoadingSpinner />;
  if (error && !data)   return <div className="text-red-400 p-6">Error: {error}</div>;
  if (!data) return null;

  const allAlerts = [
    ...data.alerts,
    ...(redis ? redis.alerts.map(a => ({ ...a, member: 'redis' })) : []),
  ];
  const overallStatus = (() => {
    const mongoDown = data.members.filter(m => !m.reachable).length;
    const redisDown = redis && !redis.reachable ? 1 : 0;
    const redAlerts = allAlerts.filter(a => a.severity === 'red').length;
    if (mongoDown > 0 || redisDown > 0 || redAlerts > 0) return { text: '🚨 DEGRADED', cls: 'bg-red-500/15 text-red-400 border-red-500/30' };
    if (allAlerts.length > 0)                            return { text: '⚠️ WARNING',  cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
    return { text: '✅ HEALTHY', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
  })();

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div className={`rounded-2xl border p-4 flex items-center justify-between ${overallStatus.cls}`}>
        <div>
          <p className="text-sm uppercase tracking-wider opacity-80">Replica Set {data.set}</p>
          <p className="text-2xl font-bold mt-1">{overallStatus.text}</p>
        </div>
        <div className="text-right text-xs opacity-80">
          <p>Last update: {lastFetch?.toLocaleTimeString()}</p>
          <p>Auto-refresh every 10s</p>
        </div>
      </div>

      {/* Active alerts */}
      {allAlerts.length > 0 && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Active Alerts ({allAlerts.length})</h3>
          <div className="space-y-2">
            {allAlerts.map((a: any, i) => (
              <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${a.severity === 'red' ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                <span className={`text-xs font-bold uppercase ${a.severity === 'red' ? 'text-red-400' : 'text-amber-400'}`}>
                  {a.severity === 'red' ? '🚨' : '⚠️'} {a.severity}
                </span>
                <span className="text-xs text-gray-400">{a.member || 'redis'}/{a.metric}</span>
                <span className="text-sm text-white flex-1">{a.message}</span>
                <span className="text-xs font-mono text-gray-300">{a.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time-series charts */}
      <DatabaseHistoryCharts />

      {/* MongoDB member cards */}
      <div>
        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">MongoDB Replica Set — Live</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {data.members.map((m) => <MemberCard key={m.name} m={m} />)}
        </div>
      </div>

      {/* Redis card */}
      {redis && (
        <div>
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Cache</h2>
          <RedisCard r={redis} />
        </div>
      )}

      {/* Slow Queries */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Slow Queries Right Now (&ge;1s)</h3>
        {data.slowOps.length === 0 ? (
          <p className="text-emerald-400 text-sm">✅ No slow queries currently running</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-white/10">
                  <th className="pb-2 font-medium">Duration</th>
                  <th className="pb-2 font-medium">Op</th>
                  <th className="pb-2 font-medium">Namespace</th>
                  <th className="pb-2 font-medium">Plan</th>
                  <th className="pb-2 font-medium text-right">Docs Examined</th>
                  <th className="pb-2 font-medium text-right">Keys Examined</th>
                </tr>
              </thead>
              <tbody>
                {data.slowOps.map((o, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className={`py-2 font-mono ${o.secs > 5 ? 'text-red-400' : 'text-amber-400'}`}>{o.secs}s</td>
                    <td className="py-2 text-gray-300">{o.op}</td>
                    <td className="py-2 text-white">{o.ns}</td>
                    <td className="py-2 text-gray-400 text-xs">{o.plan}</td>
                    <td className="py-2 text-right text-gray-300">{formatNumber(o.docsExamined)}</td>
                    <td className="py-2 text-right text-gray-300">{formatNumber(o.keysExamined)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseTab;
